import { BaseService } from './BaseService';
import type {
  CreateGuardrailBindingRequest,
  DetectorInfoDto,
  GuardrailBindingDto,
  GuardrailDto,
  GuardrailExecutionDto,
  GuardrailFilters,
  PromoteGuardrailVersionRequest,
  PublishGuardrailVersionRequest,
  RetireGuardrailVersionRequest,
  SetGuardrailBindingModeRequest,
  DeactivateGuardrailBindingRequest,
  ReactivateGuardrailBindingRequest,
  UnguardedTurnDto,
} from '../types/guardrail';

/**
 * Extracts the most useful message from a guardrails API error: the controller returns 400
 * with { message, errors? }, so prefer the response body over the generic axios message.
 */
export function getGuardrailErrorMessage(err: unknown, fallback: string): string {
  const anyErr = err as {
    response?: { data?: { message?: string; errors?: string[] } };
    message?: string;
  };
  const data = anyErr?.response?.data;
  if (data?.message) {
    const extra = data.errors?.filter((e) => e && e !== data.message) ?? [];
    return extra.length ? `${data.message}: ${extra.join('; ')}` : data.message;
  }
  return anyErr?.message || fallback;
}

/**
 * Guardrail service for the Guardrails module (registry, bindings, executions).
 */
export class GuardrailService extends BaseService {
  /**
   * Lists current guardrail versions, optionally filtered by type key or lifecycle status.
   */
  async getGuardrails(filters?: GuardrailFilters): Promise<GuardrailDto[]> {
    const queryParams = new URLSearchParams();
    if (filters?.typeKey) queryParams.append('typeKey', filters.typeKey);
    if (filters?.status !== undefined) queryParams.append('status', filters.status.toString());

    const url = `/guardrails${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<GuardrailDto[]>(url);
  }

  /**
   * Lists every version of one guardrail key, newest first.
   */
  async getVersions(guardrailKey: string): Promise<GuardrailDto[]> {
    return this.get<GuardrailDto[]>(`/guardrails/${encodeURIComponent(guardrailKey)}/versions`);
  }

  /**
   * Publishes a new Draft version of a guardrail.
   */
  async publishVersion(data: PublishGuardrailVersionRequest): Promise<GuardrailDto> {
    return this.post<GuardrailDto>('/guardrails', data);
  }

  /**
   * Promotes a Draft version to Active with the approval stamp.
   */
  async promoteVersion(
    guardrailKey: string,
    version: number,
    data: PromoteGuardrailVersionRequest
  ): Promise<GuardrailDto> {
    // guardrailKey/version are required properties on the server command, so they must be
    // present in the body for deserialization; the controller then re-stamps them from the route.
    return this.post<GuardrailDto>(
      `/guardrails/${encodeURIComponent(guardrailKey)}/versions/${version}/promote`,
      { ...data, guardrailKey, version }
    );
  }

  /**
   * Retires a version. Rejected while active bindings still depend on it.
   */
  async retireVersion(
    guardrailKey: string,
    version: number,
    data: RetireGuardrailVersionRequest
  ): Promise<GuardrailDto> {
    return this.post<GuardrailDto>(
      `/guardrails/${encodeURIComponent(guardrailKey)}/versions/${version}/retire`,
      { ...data, guardrailKey, version }
    );
  }

  /**
   * Lists binding revisions, optionally filtered by agent definition.
   */
  async getBindings(agentDefinitionId?: string): Promise<GuardrailBindingDto[]> {
    const queryParams = new URLSearchParams();
    if (agentDefinitionId) queryParams.append('agentDefinitionId', agentDefinitionId);

    const url = `/guardrails/bindings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<GuardrailBindingDto[]>(url);
  }

  /**
   * Creates a new binding revision (append-only).
   */
  async createBinding(data: CreateGuardrailBindingRequest): Promise<GuardrailBindingDto> {
    return this.post<GuardrailBindingDto>('/guardrails/bindings', data);
  }

  /**
   * Switches a binding between Shadow and Enforce as a new revision.
   */
  async setBindingMode(
    bindingKey: string,
    data: SetGuardrailBindingModeRequest
  ): Promise<GuardrailBindingDto> {
    return this.post<GuardrailBindingDto>(
      `/guardrails/bindings/${encodeURIComponent(bindingKey)}/mode`,
      { ...data, bindingKey }
    );
  }

  /**
   * Deactivates a binding so it no longer resolves at evaluation time.
   */
  async deactivateBinding(
    bindingKey: string,
    data: DeactivateGuardrailBindingRequest
  ): Promise<GuardrailBindingDto> {
    return this.post<GuardrailBindingDto>(
      `/guardrails/bindings/${encodeURIComponent(bindingKey)}/deactivate`,
      { ...data, bindingKey }
    );
  }

  /**
   * Reactivates a deactivated binding, restoring its prior enforcement settings.
   */
  async reactivateBinding(
    bindingKey: string,
    data: ReactivateGuardrailBindingRequest
  ): Promise<GuardrailBindingDto> {
    return this.post<GuardrailBindingDto>(
      `/guardrails/bindings/${encodeURIComponent(bindingKey)}/reactivate`,
      { ...data, bindingKey }
    );
  }

  /**
   * Gets every execution row of one stage evaluation group.
   */
  async getStageExecutions(stageCorrelationId: string): Promise<GuardrailExecutionDto[]> {
    return this.get<GuardrailExecutionDto[]>(`/guardrails/executions/stage/${stageCorrelationId}`);
  }

  /**
   * Gets a detector's capability declaration and config schema.
   */
  async getDetectorSchema(typeKey: string): Promise<DetectorInfoDto> {
    return this.get<DetectorInfoDto>(`/guardrails/detectors/${encodeURIComponent(typeKey)}/schema`);
  }

  /**
   * Gets the unguarded-turn sentinels (turns that ran with zero guardrail bindings resolved).
   */
  async getUnguardedTurns(sinceUtc?: string, take?: number): Promise<UnguardedTurnDto[]> {
    const queryParams = new URLSearchParams();
    if (sinceUtc) queryParams.append('sinceUtc', sinceUtc);
    if (take !== undefined) queryParams.append('take', take.toString());

    const url = `/guardrails/unguarded-turns${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<UnguardedTurnDto[]>(url);
  }
}

export const guardrailService = new GuardrailService();
